import type { Selector } from 'reselect';

import { Brand, FeatureFlag, Site } from '@swf/core/enums';
import type { ModelConfig, RootState, SelectorCreator, Slicer } from '@swf/core/store';
import { select } from '@swf/core/store';

import acceptRacesTerms from './acceptRacesTerms';
import fetchAppData from './fetchAppData';
import fetchFirstFsb from './fetchFirstFsb';
import fetchPlayerState from './fetchPlayerState';
import launch from './launch';
import open from './open';
import play from './play';
import race from './race';
import reconnect from './reconnect';
import toggleFavoriteGame from './toggleFavoriteGame';

export interface CasinoState {
  launchCount: number;
  UKTermsAccepted: boolean;
  liveRacesTermsAccepted: boolean;
  racesTermsVersionAccepted: number;
}

const reducers = {
  addLaunch: (state: CasinoState): CasinoState => ({
    ...state,
    launchCount: state.launchCount + 1
  }),
  set: (state: CasinoState, payload: Partial<CasinoState>): CasinoState => ({
    ...state,
    ...payload
  })
};

const effects = {
  acceptRacesTerms,
  fetchAppData,
  fetchFirstFsb,
  fetchPlayerState,
  launch,
  open,
  play,
  race,
  reconnect,
  toggleFavoriteGame
};

export const selectors = (slice: Slicer<CasinoState>, createSelector: SelectorCreator) => ({
  getLaunchCount() {
    return slice((state) => state.launchCount);
  },
  UKTermsAccepted() {
    return slice((state) => state.UKTermsAccepted);
  },
  liveRacesTermsAccepted() {
    return slice((state) => state.liveRacesTermsAccepted);
  },
  racesTermsVersionAccepted() {
    return slice((state) => state.racesTermsVersionAccepted);
  },
  acceptedRacesTermsAreCurrent(this: { racesTermsVersionAccepted: Selector<RootState, number> }) {
    return createSelector(
      slice,
      this.racesTermsVersionAccepted,
      (state: RootState, latestTerms: number) => latestTerms,
      (state, accepted, latestTerms) => accepted === latestTerms
    );
  },
  raceTermsAcceptanceRequired() {
    return createSelector(
      slice,
      (state: RootState, isLiveRace: boolean) => isLiveRace,
      (state) => select.feature.check(state, FeatureFlag.CasinoSlotRacesTCReqd),
      (state) => select.feature.check(state, FeatureFlag.CasinoLiveRacesTCReqd),
      (state, isLiveRace, slotFlag: boolean, liveFlag: boolean) =>
        (!isLiveRace && slotFlag) || (isLiveRace && liveFlag)
    );
  },
  stifleUrgencyRequired() {
    return createSelector(
      slice,
      (rootState) => select.site.getId(rootState),
      (state, siteId) => [Site.UK].includes(siteId)
    );
  },
  getHostId() {
    return createSelector(
      (rootState: RootState) => select.site.getBaseBrand(rootState),
      (rootState: RootState) => select.site.showVegasContent(rootState),
      (rootState: RootState) => select.site.getId(rootState),
      (brand, showVegasContent, siteId) => {
        if (siteId === Site.DESH || siteId === Site.DE) return 2;
        if (showVegasContent) return 8;

        return {
          [Brand.PokerStars]: 2,
          [Brand.StarsCasino]: 7,
          [Brand.BetStars]: 6,
          [Brand.FullTilt]: 5,
          [Brand.Unknown]: 2,
          [Brand.PokerStarsVegas]: 8,
          [Brand.SkyBet]: 9,
          [Brand.FoxBet]: 10
        }[brand];
      }
    );
  }
});

interface CasinoModel extends ModelConfig<CasinoState> {
  reducers: typeof reducers;
  effects: typeof effects;
  selectors: typeof selectors;
}

const casino: CasinoModel = {
  state: {
    launchCount: 0,
    UKTermsAccepted: false,
    liveRacesTermsAccepted: false,
    racesTermsVersionAccepted: 0
  },
  reducers,
  effects,
  selectors
};

export default casino;
